import { ContentState, Editor, EditorState, RichUtils, convertFromRaw, convertToRaw } from 'draft-js';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { LocalizeContext } from 'react-locale-language';
import './editor.css';
import Toolbar from './toolbar/Toolbar';
import useDebouncedEffect from '../../../hooks/useDebouncedEffect';

const shouldSkipContentUpdate = (autoSave, editorState, richText) => {
  if (!autoSave || !editorState) return false;

  // Always allow updates when richText prop changes (different strategy)
  if (richText !== undefined) return false;

  const currentContent = convertToRaw(editorState.getCurrentContent());
  return currentContent?.blocks?.length && currentContent.blocks.some(block => block.text.trim() !== '');
};

const createContentFromInput = richText => {
  try {
    if (typeof richText === 'string') {
      return ContentState.createFromText(richText);
    } else if (richText && typeof richText === 'object') {
      return convertFromRaw({ ...richText, entityMap: richText.entityMap || {} });
    } else {
      return ContentState.createEmpty();
    }
  } catch (error) {
    console.warn('Failed to create content from input:', error);
    return ContentState.createEmpty();
  }
};

const RichTextEditor = ({
  richText,
  onSubmit,
  disabled,
  editable = true,
  className = '',
  hideToolbar = false,
  autoSave = false,
  focusOnMount = true,
  minHeight = 150,
  placeholder,
  compKey = null
}) => {
  const [editorState, setEditorState] = useState();
  const { translate } = useContext(LocalizeContext);
  const contentRef = useRef(null);
  const compKeyRef = useRef(null);

  // Content synchronization
  useEffect(() => {
    if (shouldSkipContentUpdate(autoSave, editorState, richText)) {
      return;
    }

    const newContent = createContentFromInput(richText);

    if (
      (compKeyRef?.current && compKeyRef.current === compKey) ||
      (contentRef?.current && contentRef.current === newContent)
    ) {
      return;
    }

    if (editorState) {
      const selection = editorState.getSelection();
      const newEditorState = EditorState.createWithContent(newContent);
      const newStateWithSelection = EditorState.forceSelection(newEditorState, selection);
      setEditorState(newStateWithSelection);
    } else {
      setEditorState(EditorState.createWithContent(newContent));
    }

    contentRef.current = newContent;
  }, [richText, autoSave]);

  const editor = useRef(null);

  useEffect(() => {
    if (focusOnMount) {
      focusEditor();
    }
  }, [focusOnMount]);

  const handleEditorChange = newEditorState => {
    setEditorState(newEditorState);
  };

  useDebouncedEffect(
    () => {
      if (autoSave && onSubmit && editorState) {
        const currentContent = editorState.getCurrentContent();
        compKeyRef.current = compKey;
        if (contentRef.current !== currentContent) {
          contentRef.current = currentContent;
          onSubmit(convertToRaw(currentContent));
        }
      }
    },
    [editorState],
    750
  );

  const focusEditor = () => {
    editor.current?.focus();
  };

  const handleKeyCommand = command => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return true;
    }
    return false;
  };

  // FOR INLINE STYLES
  const styleMap = {
    CODE: {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
      fontSize: 16,
      padding: 2
    },
    HIGHLIGHT: {
      backgroundColor: '#b4f8c8'
    },
    UPPERCASE: {
      textTransform: 'uppercase'
    },
    LOWERCASE: {
      textTransform: 'lowercase'
    },
    CODEBLOCK: {
      fontFamily: '"fira-code", "monospace"',
      fontSize: 'inherit',
      background: '#ffeff0',
      fontStyle: 'italic',
      lineHeight: 1.5,
      padding: '0.3rem 0.5rem',
      borderRadius: ' 0.2rem'
    },
    SUPERSCRIPT: {
      verticalAlign: 'super',
      fontSize: '80%'
    },
    SUBSCRIPT: {
      verticalAlign: 'sub',
      fontSize: '80%'
    }
  };

  // FOR BLOCK LEVEL STYLES(Returns CSS Class From DraftEditor.css)
  const myBlockStyleFn = contentBlock => {
    const type = contentBlock.getType();
    switch (type) {
      case 'blockQuote':
        return 'superFancyBlockquote';
      case 'leftAlign':
        return 'leftAlign';
      case 'rightAlign':
        return 'rightAlign';
      case 'centerAlign':
        return 'centerAlign';
      case 'justifyAlign':
        return 'justifyAlign';
      default:
        break;
    }
  };

  if (!editorState) return '';

  return (
    <>
      <div className="editor-wrapper" onClick={focusEditor}>
        {editable && !hideToolbar && <Toolbar editorState={editorState} setEditorState={setEditorState} />}
        <div
          style={{ minHeight: minHeight || 'auto', fontSize: '12px' }}
          className={`border rounded p-3 bg-light ${className}`}
        >
          <Editor
            ref={editor}
            handleKeyCommand={handleKeyCommand}
            editorState={editorState}
            customStyleMap={styleMap}
            readOnly={!editable}
            blockStyleFn={myBlockStyleFn}
            onChange={!editable ? undefined : handleEditorChange}
            placeholder={placeholder}
          />
        </div>
      </div>
      {editable && !autoSave && (
        <div className="text-end mt-2 mb-4">
          <Button
            className="text-white"
            size="sm"
            variant="success"
            disabled={disabled}
            onClick={() => onSubmit(convertToRaw(editorState.getCurrentContent()))}
          >
            {translate('save')}
          </Button>
        </div>
      )}
    </>
  );
};

export default RichTextEditor;
